import { ReactNode, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { UserProfile } from "@/components/UserProfile";
import { AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import { Button } from "@/components/ui/button";
import { Accessibility } from "lucide-react";
import { useTranslation } from "react-i18next";

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation();
  const [accessibilityToolbarOpen, setAccessibilityToolbarOpen] = useState(false);
  
  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full max-w-full overflow-x-hidden">
        <div className="flex-1 flex flex-col max-w-full overflow-x-hidden">
          <header 
            id="main-navigation"
            className="h-16 border-b border-border/90 bg-background/80 backdrop-blur-xl flex items-center justify-between px-4 md:px-6 sticky top-0 z-40"
            role="banner"
          >
            <div className="flex items-center">
              <SidebarTrigger className="h-9 w-9 rounded-lg border border-border/80 hover:bg-secondary/80" />
              <div className="ml-4">
                <h1 className="text-lg font-semibold tracking-tight text-foreground">{t('app.title')}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAccessibilityToolbarOpen(!accessibilityToolbarOpen)}
                aria-label={t('accessibility.toolbar.title', 'נגישות')}
                aria-expanded={accessibilityToolbarOpen}
                className="no-print"
              >
                <Accessibility className="h-4 w-4" />
              </Button>
              <LanguageSwitcher />
              <UserProfile />
            </div>
          </header>
          
          <main 
            id="main-content"
            className="flex-1 p-3 md:p-6 max-w-full overflow-x-hidden"
            role="main"
            tabIndex={-1}
          >
            {children}
          </main>
        </div>
        
        <AppSidebar />
      </div>
      
      <AccessibilityToolbar 
        isOpen={accessibilityToolbarOpen}
        onClose={() => setAccessibilityToolbarOpen(false)}
      />
    </SidebarProvider>
  );
}